<template>
  <ResponsiveLayout
    spread-actions
    :progress="progress"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{
            !shouldCollectName
              ? $t('eu__doctor_outreach_personal_information__title__web')
              : $t('eu__doctor_outreach_personal_information__title')
          }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{
            !shouldCollectName
              ? $t('eu__doctor_outreach_personal_information__description__web')
              : $t('eu__doctor_outreach_personal_information__description')
          }}
        </p>
      </div>
      <form
        id="doctor-outreach-form"
        data-qa="doctor-outreach-form"
        class="kds-content-block"
        autocomplete="off"
        @submit.prevent="submit"
      >
        <TextField
          v-if="shouldCollectName"
          :label="$t('eu__doctor_outreach_personal_information__first_name')"
          data-qa="first-name"
          autocomplete="given-name"
          v-bind="fieldAttrs.firstName"
        />
        <TextField
          v-if="shouldCollectName"
          :label="$t('eu__doctor_outreach_personal_information__last_name')"
          data-qa="last-name"
          :hint="
            $t('eu__doctor_outreach_personal_information__last_name__hint')
          "
          autocomplete="family-name"
          v-bind="fieldAttrs.lastName"
        />
        <DateOfBirthField
          lang="de-DE"
          v-bind="fieldAttrs.dateOfBirth"
        />
        <BaseBanner variant="neutral">
          <DocumentViewer
            class="kds-typography-paragraph-medium"
            :link="consentDescription"
          />
        </BaseBanner>
      </form>
    </div>

    <BaseModal
      ref="errorModalRef"
      :title="$t('generic__error_title')"
    >
      <p
        class="kds-typography-paragraph-large"
        v-html="$t('generic__error_message', { email: env.VITE_SUPPORT_EMAIL })"
      />
      <template #footer>
        <div class="doctor-outreach__modal-actions">
          <BaseButton
            size="small"
            variant="outlined"
            data-qa="prescription-consent-error-cancel"
            @click="handleErrorModalDismiss"
          >
            {{ $t('generic__third_party_url_confirmation__secondary_label') }}
          </BaseButton>
          <BaseButton
            size="small"
            data-qa="prescription-consent-error-retry"
            @click="handleRequestRetry"
          >
            {{ $t('generic__try_again') }}
          </BaseButton>
        </div>
      </template>
    </BaseModal>

    <template #actions>
      <BaseButton
        :text="$t('eu__doctor_outreach_consent__submit')"
        data-qa="submit"
        v-bind="submitAttrs"
        form="doctor-outreach-form"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import env from '@shared/env.js'
import BaseModal from '@shared/components/BaseModal.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import DocumentViewer from '@shared/components/DocumentViewer.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import TextField from '@shared/components/form/TextField.vue'
import DateOfBirthField from '@shared/components/form/DateOfBirthField.vue'
import useForm from '@shared/composables/useForm.js'
import useDoctorOutreachStore from '@eu/app/stores/useDoctorOutreachStore.js'
import useEuStore from '@eu/app/stores/useEuStore.js'
import * as apiRequest from '@shared/api-endpoints/index.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import useUserStore from '@shared/stores/useUserStore.js'
import usePreSubscriptionWebOptimizedFunnel from '@shared/composables/usePreSubscriptionWebOptimizedFunnel'
import useIsResubscription from '@shared/composables/useIsResubscription.ts'
import defineLanguage from '@shared/utils/defineLanguage.js'
import Tracker from '@shared/Tracker.js'

const { t } = useI18n()
const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.DOCTOR_OUTREACH_CONSENT,
  ROUTE.DOCTOR_OUTREACH_CONSENT_CONFIRMATION,
  ROUTE.DOCTOR_OUTREACH_CONSENT_MANUAL,
)
const doctorOutreachStore = useDoctorOutreachStore()
const euStore = useEuStore()
const userStore = useUserStore()
const preSubscriptionWebOptimizedFunnel = usePreSubscriptionWebOptimizedFunnel()
const isResubscription = useIsResubscription()

// Renewal requests carry the user's therapy progress to the practice, so the
// consent has to cover that on top of the standard prescription authorisation.
const consentDescriptionKey = computed(() =>
  isResubscription.value
    ? 'eu__doctor_outreach_consent__resubscription__description'
    : 'eu__doctor_outreach_consent__description',
)

const consentDescription = computed(() => {
  const privacyLink = `${env.VITE_PUBLIC_PATH}static/${defineLanguage(userStore.lang)}/privacy-website/index.html`
  return t(consentDescriptionKey.value, { privacyLink })
})

const createTimelineEntries = async () => {
  try {
    await apiRequest.updateUserConsents([
      { type: 'doctor_outreach', given: true },
    ])
    await apiRequest.sendSubscriptionActivationEntry({
      type: 'doctor_outreach_consent_given',
    })
  } catch {
    // ignore -> timeline entries are non-critical
  }
}

// Collect the name unless it was already captured upstream. In the pre-subscription web-optimized
// funnel it's entered earlier at NameInput, which saves it to euStore.formData; everyone else — a
// resubscribing user (who has a subscription, so not pre-subscription), or a funnel user who somehow
// arrived without it — enters it here. We read euStore.formData (never written by this screen) rather
// than personalInformation (which the form rewrites on every keystroke), so entering the name here
// neither flips the fields mid-typing nor hides them on back-navigation.
const nameEnteredBefore = Boolean(
  euStore.formData.firstName && euStore.formData.lastName,
)
const shouldCollectName = computed(
  () => !preSubscriptionWebOptimizedFunnel.value || !nameEnteredBefore,
)

const form = ref({
  firstName:
    doctorOutreachStore.personalInformation.firstName ||
    euStore.formData.firstName ||
    '',
  lastName: doctorOutreachStore.personalInformation.lastName || '',
  dateOfBirth: doctorOutreachStore.personalInformation.dateOfBirth || '',
})

watch(
  form,
  (value) => {
    doctorOutreachStore.personalInformation = { ...value }
  },
  { deep: true },
)

const errorModalRef = ref(null)

const formConfig = computed(() => ({
  ...(shouldCollectName.value
    ? {
        firstName: { validations: ['required'] },
        lastName: { validations: ['required'] },
      }
    : {}),
  dateOfBirth: { validations: ['dob'] },
}))

const { fieldAttrs, submitAttrs, submit } = useForm(form, formConfig, {
  onSubmit: async () => {
    Tracker.trackButtonSelect(
      'doctor_outreach_consent_given',
      'doctor_outreach_consent',
    )

    const { firstName, lastName, dateOfBirth } =
      doctorOutreachStore.personalInformation
    const { prescriberRequest, selectedPracticeId } = doctorOutreachStore

    await apiRequest.submitDoctorOutreachRequest({
      firstName,
      lastName,
      dateOfBirth,
      consentGivenAt: new Date().toISOString(),
      prescriberId: selectedPracticeId ?? null,
      enteredPrescriberName: prescriberRequest?.prescriberName ?? null,
      enteredPrescriberPlace: prescriberRequest?.place ?? null,
      enteredPrescriberZipCode: prescriberRequest?.zipCode ?? null,
    })

    await createTimelineEntries()
  },
  onSuccess: () => navigateNext(),
  onError: () => {
    errorModalRef.value?.open()
  },
})

const handleErrorModalDismiss = () => {
  errorModalRef.value?.close()
}
const handleRequestRetry = () => {
  errorModalRef.value?.close()
  submit()
}
</script>

<style lang="scss" scoped>
.doctor-outreach__modal-actions {
  @include flex-container(row, flex-end, sm);
}
</style>
